/**
 * vertical 화면이 **서버를 부르는 자리.** (docs/decisions/0059)
 *
 * 이 파일이 `index.ts` 가 적어 둔 재검토 트리거의 답이다 —
 * *"vertical 화면이 처음 서버를 부르는 PR"* 에서 훅의 모양을 정한다.
 *
 * ─── 왜 raw client 를 안 내보내고 포트인가 ───
 * `rpc-client` 를 vertical 에 열면 vertical 이 자기 링크를 만들 수 있고, 그러면 스탬프
 * 관찰·401 분류·스큐 감지가 도는 통로가 둘이 된다(rpc-client 헤더 주석이 그 이유를 적어
 * 뒀다). 코어 화면은 같은 문제를 `core-web` 의 `PortsProvider` 로 풀었고 —
 * 이건 그 장치의 vertical 판이다. **다른 점 하나**: 코어의 포트는 타입이 `contracts` 에
 * 있지만(`CoreApi`), vertical 의 타입은 vertical 자신이 소유한다. contracts 가 그것을
 * 알면 `core-must-not-know-verticals` 가 뒤집힌다.
 *
 * ─── 값이 `unknown` 인 것과, 검사가 어디 있는가 ───
 * 이 맵은 `Record<모듈키, unknown>` 이다. 여기서 타입을 좁힐 방법이 없다 —
 * 좁히려면 이 패키지가 vertical 의 타입을 알아야 하기 때문이다.
 * **그래서 검사는 여기가 아니라 composition root 에 있다**: `apps/web/src/rpc.ts` 가
 * `satisfies` 로 "이 키에 넣는 값이 그 vertical 이 요구하는 모양인가"를 컴파일 시점에
 * 판정한다. 그 한 줄이 빠지면 이 훅의 제네릭은 근거 없는 단언이 된다 —
 * `createRpcClient<ClientOf<AppRouter>>()` 가 이미 같은 규약으로 서 있고(타입은 커널이,
 * 값은 rpc-client 가), 이건 그 규약을 vertical 까지 늘린 것이다.
 *
 * ★ 타입 통로는 `import type` 이라 **청크 경계를 안 넘는다.** apps/web 이 vertical 의
 *   타입을 부르는 것은 빌드 시점에 사라지고, 코드는 여전히 dynamic import 뒤에 있다.
 */
import { createContext, useContext, type ReactNode } from 'react';

/** 모듈 키 → 그 모듈의 RPC 슬라이스. 값의 타입은 composition root 가 지킨다(위 주석). */
export type ModuleRpcMap = Readonly<Record<string, unknown>>;

const ModuleRpcContext = createContext<ModuleRpcMap | null>(null);

export function ModuleRpcProvider({ apis, children }: { apis: ModuleRpcMap; children: ReactNode }) {
  return <ModuleRpcContext.Provider value={apis}>{children}</ModuleRpcContext.Provider>;
}

/**
 * 이 모듈의 RPC 슬라이스. **Provider 가 없거나 키가 없으면 던진다.**
 *
 * 기본값(빈 객체·no-op 클라이언트)을 주지 않는 이유는 `SurfaceProvider`·`PortsProvider` 와
 * 같다: 배선 사고가 "데이터가 0건"으로 위장된다. vertical 은 dynamic import 뒤에 있어서
 * 배선이 빠져도 **그 모듈을 구독한 테넌트에서만** 드러나므로, 조용하면 더 늦게 발견된다.
 */
export function useModuleRpc<T>(moduleKey: string): T {
  const map = useContext(ModuleRpcContext);
  if (!map) {
    throw new Error(
      'ModuleRpcProvider 밖에서 vertical 화면이 서버를 불렀다. ' +
        'apps/web 의 composition root 가 이 Provider 로 감싸야 한다.',
    );
  }
  if (!(moduleKey in map)) {
    throw new Error(
      `모듈 '${moduleKey}' 의 RPC 슬라이스가 배선되지 않았다. ` +
        `apps/web/src/rpc.ts 의 VERTICAL_RPC 에 이 키를 더한다 — ` +
        `화면 로더(registry.ts)만 있고 이 배선이 없으면 화면은 열리는데 데이터만 없다.`,
    );
  }
  return map[moduleKey] as T;
}
